/**
 * The blur filter — what the number is, why it is ranked rather than scored,
 * and why an unmeasured face is its own state.
 *
 * WHAT THE NUMBER IS. `FaceEmbedding.face_sharpness` is the RAW Tenengrad
 * magnitude of the eye region (sentinel `calculate_face_sharpness`): the mean
 * squared image gradient of the patch, in whatever units 8-bit pixels happen
 * to produce. It is not a 0..1 score and never was one. Its absolute size
 * moves with the face's pixel size, with ISO noise, with the lens and with
 * subject contrast — so 180 on one frame is not "worse than" 400 on another.
 *
 * WHAT IT REPLACED. The slider on this tab used to filter `embedding_quality`
 * under the label "hide blurry faces". That label was false: quality is
 * `detection_score × size_factor × alignment_factor` (seraph
 * `steps/embedding.py:134`), and none of the three measures focus. A
 * motion-smeared 200 px face with level eyes scored high and stayed; a
 * pin-sharp small face got hidden. `face_sharpness` is the metric that makes
 * the label true.
 *
 * WHY THIS FILE DOES NOT NORMALISE. Dividing by face area and subtracting a
 * per-frame ISO noise floor is what V1's consumer did, and it is the right
 * eventual answer — but the constants for it have to be measured, and that
 * measurement has not been run. Inventing one here would ship a number that
 * LOOKS calibrated and is not, which is worse than an obviously relative one.
 *
 * SO IT RANKS. The editor's real question is comparative — "of the faces in
 * front of me, which are the soft ones" — so the control is a PERCENTILE over
 * the faces currently loaded. A cut value is taken at that percentile and
 * faces strictly below it are de-emphasised. Three properties come free:
 *   - it makes no claim about what any absolute magnitude means;
 *   - percentile 0 is a true off, because nothing is strictly below the
 *     minimum of the set;
 *   - a single face, or a set where every face measures the same, dims
 *     nothing — a ranking over such a set carries no information, and
 *     pretending otherwise would invent a verdict.
 * The raw cut value is surfaced next to the percentile in the UI so the
 * arbitrary scale stays visible instead of hiding behind a tidy percentage.
 *
 * NULL IS UNMEASURED — NEVER SHARP, NEVER ZERO. Three populations carry no
 * measurement and permanently will: faces created before the field shipped
 * (forward-only, no backfill), faces from the training/gallery path (they
 * never pass through sentinel), and faces whose eye midpoint joined to no
 * person detection. The last one is the dangerous one: YOLO misses a person
 * box under occlusion, crowding and extreme motion — precisely the conditions
 * that produce the blur this filter exists to find. Reading null as 0 would
 * therefore hide exactly the faces most likely to be blurry; reading it as
 * +Infinity would present them as sharp. Both are wrong and both are silent.
 * Unmeasured is its own state: excluded from the ranking, never dimmed, and
 * rendered with a marker of its own so the editor can tell "we measured this
 * and it is fine" from "we never measured this".
 *
 * Kept in lockstep with `matrix-app/src/utils/faceSharpness.ts` — same rules,
 * same preference key, so a threshold set on one client means the same thing
 * on the other.
 */

/** `user.preferences` key holding the ratio. Shared verbatim with the app.
 * Deliberately a NEW key: the old control stored a percentile-of-count,
 * which must never be reinterpreted as a ratio-to-sharpest. */
export const FACE_SHARPNESS_PREF_KEY = 'face_sharpness_ratio';

/**
 * Highest percentile the control offers. Past this the "filter" is really
 * just emptying the grid, which is a worse tool than scrolling past.
 */
export const SHARPNESS_PERCENTILE_MAX = 0.98; // kept name for call sites; semantics = RATIO max (0.98 mirrors the app — a 0.9 clamp silently truncated prefs saved above it)

/** Slider granularity. Coarse on purpose: the scale is ordinal, not metric. */
export const SHARPNESS_PERCENTILE_STEP = 0.05;

/**
 * A value counts as a measurement only if it is a finite number. `null`,
 * `undefined` and NaN are all "we never measured this" — a NaN arriving from
 * a malformed payload is not a zero-sharpness face.
 *
 * @param {number|null|undefined} value
 * @returns {boolean}
 */
export function isSharpnessMeasured(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Clamp a percentile into the range the control actually offers.
 *
 * @param {unknown} value
 * @returns {number}
 */
export function clampSharpnessPercentile(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.min(SHARPNESS_PERCENTILE_MAX, Math.max(0, value));
}

/**
 * Cut value at `ratio` of the SHARPEST measured face, or null when nothing
 * is measured. Unmeasured entries never move the reference — it is the
 * sharpest thing we MEASURED, not a property of pipeline coverage.
 *
 * @param {Array<number|null|undefined>} values
 * @param {number} ratio
 * @returns {number|null}
 */
export function sharpnessCutoff(values, ratio) {
  // Ratio-to-sharpest (mirrors the app util): cut = ratio x max(measured).
  // The earlier percentile-of-count dimmed the runner-up on two-face sets
  // even when both faces were fine.
  const measured = values.filter(isSharpnessMeasured);
  if (measured.length === 0) return null;
  const r = clampSharpnessPercentile(ratio);
  if (r <= 0) return 0; // strictly-below zero matches nothing: a true OFF
  return r * Math.max(...measured);
}

/**
 * State of one face against a cut: 'ok' | 'soft' | 'unmeasured'.
 *
 * Strictly-below, so a face sitting exactly on the cut stays visible — at
 * percentile 0 the cut IS the minimum, and the blurriest face in the set
 * should not vanish just for being the blurriest.
 *
 * @param {number|null|undefined} value
 * @param {number|null} cutoff
 * @returns {'ok'|'soft'|'unmeasured'}
 */
export function classifySharpness(value, cutoff) {
  if (!isSharpnessMeasured(value)) return 'unmeasured';
  if (cutoff == null) return 'ok';
  return value < cutoff ? 'soft' : 'ok';
}

/**
 * Counts for the readout, computed in one pass over the same rule.
 *
 * @param {Array<number|null|undefined>} values
 * @param {number} percentile
 * @returns {{cutoff: number|null, total: number, measured: number,
 *            unmeasured: number, soft: number, shown: number}}
 */
export function summarizeSharpness(values, percentile) {
  const cutoff = sharpnessCutoff(values, percentile);
  let measured = 0;
  let soft = 0;
  for (const value of values) {
    const state = classifySharpness(value, cutoff);
    if (state === 'unmeasured') continue;
    measured += 1;
    if (state === 'soft') soft += 1;
  }
  return {
    cutoff,
    total: values.length,
    measured,
    unmeasured: values.length - measured,
    soft,
    shown: values.length - soft,
  };
}

/**
 * Raw magnitude for display. Deliberately not a percentage and not padded to
 * a fixed width: it is meant to read as an instrument value the editor can
 * compare against its neighbours, not as a grade. Unmeasured renders as a
 * dash — the one thing it must never render as is a number.
 *
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatSharpness(value) {
  if (!isSharpnessMeasured(value)) return '—';
  const magnitude = Math.abs(value);
  if (magnitude >= 100) return String(Math.round(value));
  if (magnitude >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

/**
 * Slider caption. Says what the control does — dims a SHARE of the set — and
 * never implies a calibrated threshold. Zero reads OFF rather than "0%", so
 * the off position is unmistakable.
 *
 * @param {number} percentile
 * @returns {string}
 */
export function sharpnessPercentileLabel(percentile) {
  const p = clampSharpnessPercentile(percentile);
  if (p <= 0) return 'OFF';
  return `SOFTEST ${Math.round(p * 100)}%`;
}
